import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { newsletterAPI } from '../utils/api';
import Button from '../components/Button';

const UnsubscribePage: React.FC = () => {
  const [email, setEmail] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    // Extract email from URL query parameters
    const searchParams = new URLSearchParams(location.search);
    const emailParam = searchParams.get('email');
    
    if (emailParam) {
      setEmail(emailParam);
      handleUnsubscribe(emailParam);
    }
  }, [location.search]);

  const handleUnsubscribe = async (emailToUnsubscribe: string) => {
    try {
      setLoading(true);
      setError(null);
      
      await newsletterAPI.unsubscribe(emailToUnsubscribe);
      
      setSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Der opstod en fejl ved afmelding af nyhedsbrevet.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-snudefryd-beige min-h-screen py-12">
      <div className="container-custom max-w-2xl">
        <div className="bg-white rounded-lg shadow-md p-8">
          <h1 className="text-2xl md:text-3xl font-bold text-center mb-6">Afmeld Nyhedsbrev</h1>
          
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-snudefryd-blue mx-auto"></div>
              <p className="mt-4 text-gray-600">Behandler din anmodning...</p>
            </div>
          ) : success ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-gray-800 mb-2">Afmelding Gennemført</h2>
              <p className="text-gray-600 mb-6">
                Du er nu afmeldt vores nyhedsbrev. Vi er kede af at se dig gå, men du er altid velkommen tilbage.
              </p>
              <Button variant="primary" onClick={() => window.location.href = '/'}>
                Gå til forsiden
              </Button>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-gray-800 mb-2">Der opstod en fejl</h2>
              <p className="text-red-600 mb-6">{error}</p>
              <Button variant="primary" onClick={() => window.location.reload()}>
                Prøv igen
              </Button>
            </div>
          ) : (
            <div className="py-4">
              <p className="text-gray-600 mb-6 text-center">
                Indtast din email-adresse nedenfor for at afmelde dig vores nyhedsbrev.
              </p>
              <div className="mb-6">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-snudefryd-blue focus:border-snudefryd-blue"
                  placeholder="din@email.dk"
                  required
                />
              </div>
              <Button
                variant="primary"
                onClick={() => handleUnsubscribe(email)}
                className="w-full"
                disabled={!email}
              >
                Afmeld Nyhedsbrev
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UnsubscribePage; 